import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiCopy, FiCheckCircle, FiExternalLink, FiGlobe, FiEye, FiShield, FiAward, FiLock, FiStar, FiLayout, FiAlertTriangle } from 'react-icons/fi';

export const PAGE_LAYOUTS = [
  { value: 'masonry', label: 'Masonry' },
  { value: 'grid', label: 'Grid' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'wall', label: 'Animated Wall' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'spotlight', label: 'Spotlight' },
  { value: 'bento', label: 'Bento' },
  { value: 'marquee', label: 'Marquee' },
  { value: 'polaroid', label: 'Polaroid' },
  { value: 'chat', label: 'Chat Stream' },
  { value: 'magazine', label: 'Magazine' }
];

export const REVIEW_ORDERS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'random', label: 'Random shuffle' },
  { value: 'highest', label: 'Highest rated first' },
  { value: 'detailed', label: 'Most detailed first' },
  { value: 'video_first', label: 'Videos first' },
  { value: 'platform_mix', label: 'Platform mix' },
  { value: 'google_first', label: 'Google first' },
  { value: 'trustpilot_first', label: 'Trustpilot first' },
  { value: 'clutch_first', label: 'Clutch first' }
];

// Skeleton blocks arranged in the shape of each layout — a placeholder
// preview, not a live render of real reviews.
const LayoutPreview = ({ layout }) => {
  const Box = ({ className = '', style }) => (
    <div className={`rounded-md bg-zinc-800/80 border border-white/5 ${className}`} style={style} />
  );

  const previews = {
    masonry: (
      <div className="grid grid-cols-3 gap-2 w-full">
        <div className="space-y-2"><Box className="h-16" /><Box className="h-10" /><Box className="h-14" /></div>
        <div className="space-y-2"><Box className="h-10" /><Box className="h-16" /><Box className="h-12" /></div>
        <div className="space-y-2"><Box className="h-14" /><Box className="h-12" /><Box className="h-16" /></div>
      </div>
    ),
    grid: (
      <div className="grid grid-cols-3 gap-2 w-full">
        {[...Array(6)].map((_, i) => <Box key={i} className="h-14" />)}
      </div>
    ),
    carousel: (
      <div className="flex gap-2 w-full overflow-hidden">
        <Box className="h-24 w-1/3 flex-shrink-0" />
        <Box className="h-24 w-1/3 flex-shrink-0" />
        <Box className="h-24 w-1/3 flex-shrink-0" />
        <Box className="h-24 w-1/3 flex-shrink-0 opacity-40" />
      </div>
    ),
    wall: (
      <div className="grid grid-cols-3 gap-2 w-full overflow-hidden h-36">
        {[0, 1, 2].map(col => (
          <div key={col} className="space-y-2" style={{ animation: `mot-preview-scroll 5s linear infinite ${col * -1.6}s` }}>
            {[...Array(4)].map((_, i) => <Box key={i} className="h-12" />)}
          </div>
        ))}
        <style>{`@keyframes mot-preview-scroll { from { transform: translateY(0); } to { transform: translateY(-40%); } }`}</style>
      </div>
    ),
    timeline: (
      <div className="relative w-full py-1">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-orange-500/30" />
        <div className="space-y-2">
          <div className="flex"><Box className="h-10 w-[45%]" /></div>
          <div className="flex justify-end"><Box className="h-10 w-[45%]" /></div>
          <div className="flex"><Box className="h-10 w-[45%]" /></div>
        </div>
      </div>
    ),
    spotlight: (
      <div className="space-y-2 w-full">
        <Box className="h-20 w-full border-orange-500/30" />
        <div className="grid grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => <Box key={i} className="h-8" />)}
        </div>
      </div>
    ),
    bento: (
      <div className="grid grid-cols-3 grid-rows-2 gap-2 w-full h-32">
        <Box className="col-span-2 row-span-1" />
        <Box />
        <Box />
        <Box className="col-span-2 row-span-1" />
      </div>
    ),
    marquee: (
      <div className="space-y-2 w-full overflow-hidden">
        <div className="flex gap-2" style={{ animation: 'mot-preview-marquee 6s linear infinite' }}>
          {[...Array(6)].map((_, i) => <Box key={i} className="h-12 w-24 flex-shrink-0" />)}
        </div>
        <div className="flex gap-2" style={{ animation: 'mot-preview-marquee 6s linear infinite reverse' }}>
          {[...Array(6)].map((_, i) => <Box key={i} className="h-12 w-24 flex-shrink-0" />)}
        </div>
        <style>{`@keyframes mot-preview-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </div>
    ),
    polaroid: (
      <div className="grid grid-cols-3 gap-3 w-full px-2">
        <Box className="h-16 -rotate-3" />
        <Box className="h-16 rotate-2" />
        <Box className="h-16 -rotate-1" />
        <Box className="h-16 rotate-3" />
        <Box className="h-16 -rotate-2" />
        <Box className="h-16 rotate-1" />
      </div>
    ),
    chat: (
      <div className="space-y-2 w-full max-w-[220px] mx-auto">
        <Box className="h-9 w-3/4 rounded-2xl rounded-bl-sm" />
        <div className="flex justify-end"><Box className="h-9 w-3/4 rounded-2xl rounded-br-sm border-orange-500/20" /></div>
        <Box className="h-9 w-2/3 rounded-2xl rounded-bl-sm" />
      </div>
    ),
    magazine: (
      <div className="space-y-2 w-full">
        <div className="space-y-1.5 px-4 py-2">
          <div className="h-2 bg-zinc-700 rounded w-3/4 mx-auto" />
          <div className="h-2 bg-zinc-700 rounded w-1/2 mx-auto" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[...Array(4)].map((_, i) => <Box key={i} className="h-10" />)}
        </div>
      </div>
    )
  };

  return previews[layout] || previews.masonry;
};

const MyPage = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLayout, setPageLayout] = useState('masonry');
  const [reviewOrder, setReviewOrder] = useState('newest');
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [layoutSaveState, setLayoutSaveState] = useState(''); // '', 'saved', 'error'

  useEffect(() => {
    if (!user?.id) return;

    const fetchPageData = async () => {
      try {
        const [profileRes, seoRes] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', user.id).single(),
          supabase.from('seo_settings').select('*').eq('user_id', user.id).maybeSingle()
        ]);

        if (profileRes.data) setProfile(profileRes.data);
        if (seoRes.data) {
          setSeo(seoRes.data);
          setPageLayout(seoRes.data.page_layout || 'masonry');
          setReviewOrder(seoRes.data.page_review_order || 'newest');
        }
      } catch (err) {
        console.error('Error fetching MyPage data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [user?.id]);

  const publicUrl = profile?.public_id 
    ? `https://moment.reviews/p/${profile.public_id}` 
    : `https://moment.reviews/p/setup-your-id`;

  const handleCopy = () => {
    if (!profile?.public_id) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveLayout = async () => {
    if (!user?.id) return;
    setIsSavingLayout(true);
    setLayoutSaveState('');
    try {
      const { error } = await supabase.from('seo_settings').upsert({
        user_id: user.id,
        page_layout: pageLayout,
        page_review_order: reviewOrder
      }, { onConflict: 'user_id' });
      if (error) throw error;
      setLayoutSaveState('saved');
    } catch (err) {
      console.error('Error saving page layout:', err);
      setLayoutSaveState('error');
    } finally {
      setIsSavingLayout(false);
      setTimeout(() => setLayoutSaveState(''), 2500);
    }
  };

  const hasBlueBadge = profile?.has_verified_business || false;
  const hasGreenBadge = profile?.has_verified_reviews || false;
  const hasGoldBadge = user?.is_pro || false; // Evaluated dynamically via AuthContext

  const badges = [
    {
      id: 'blue',
      name: 'Verified Business',
      icon: FiShield,
      unlocked: hasBlueBadge,
      colorClass: 'bg-blue-500/10 border-blue-500/20 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)]',
      description: 'Verify your business by connecting your official domain to Moment of Truth.'
    },
    {
      id: 'green',
      name: 'Verified Reviews',
      icon: FiCheckCircle,
      unlocked: hasGreenBadge,
      colorClass: 'bg-green-500/10 border-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.1)]',
      description: 'Keep this badge by ensuring 100% of your reviews are pulled from verified sources.'
    },
    {
      id: 'gold',
      name: 'Committed to Truth',
      icon: FiStar,
      unlocked: hasGoldBadge,
      colorClass: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.1)]',
      description: 'Show your commitment to transparency by joining our advocacy program or upgrading to a premium account.'
    }
  ];

  const defaultCompanyName = seo?.company_name || profile?.full_name || 'Your Company';
  const defaultTitle = `${defaultCompanyName} Reviews & Testimonials | Moment of Truth`;
  const defaultDesc = `Explore real reviews and testimonials from ${defaultCompanyName} customers. A simple, transparent Wall of Love built from genuine client feedback.`;

  if (loading) {
    return (
      <div className="py-20 flex justify-center w-full">
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto pb-12"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div className="space-y-6">
          {/* Page Layout Card */}
          <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
                <SafeIcon icon={FiLayout} className="text-orange-400 text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-50">Page Layout</h3>
                <p className="text-sm text-zinc-400">Choose how reviews appear on your public page.</p>
              </div>
            </div>

            <label className="text-xs text-zinc-500 font-semibold tracking-wider uppercase mb-3 block">Layout</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {PAGE_LAYOUTS.map(l => {
                // Server-enforced too: free pages always render grid/masonry.
                const locked = user?.tier === 'free' && !['grid', 'masonry'].includes(l.value);
                return (
                  <button
                    key={l.value}
                    onClick={() => !locked && setPageLayout(l.value)}
                    disabled={locked}
                    title={locked ? 'Upgrade to Pro to unlock this layout' : undefined}
                    className={`relative px-2 py-2 rounded-lg text-xs font-medium transition-all duration-300 border ${
                      locked
                        ? 'bg-[#0a0500]/30 text-zinc-600 border-white/5 cursor-not-allowed'
                        : pageLayout === l.value
                        ? 'bg-orange-500/20 text-amber-100 border-orange-500/50 shadow-[0_0_10px_rgba(234,88,12,0.15)]'
                        : 'bg-[#0a0500]/50 text-zinc-400 border-white/5 hover:border-orange-500/30 hover:text-zinc-200'
                    }`}
                  >
                    {l.label}
                    {locked && (
                      <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 text-[8px] font-bold uppercase tracking-wide">
                        Pro
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {user?.tier === 'free' && (
              <p className="text-[11px] text-zinc-500 mb-4">
                Free plan includes Grid and Masonry. <span className="text-orange-400">Upgrade to Pro</span> to unlock all {PAGE_LAYOUTS.length} layouts.
              </p>
            )}

            <label className="text-xs text-zinc-500 font-semibold tracking-wider uppercase mb-3 block">Review Order</label>
            <select
              value={pageLayout === 'timeline' ? 'newest' : reviewOrder}
              onChange={(e) => setReviewOrder(e.target.value)}
              disabled={pageLayout === 'timeline'}
              className="w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl px-4 py-3 text-sm text-zinc-200 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mb-2"
            >
              {REVIEW_ORDERS.map(o => (
                <option key={o.value} value={o.value} className="bg-zinc-900">{o.label}</option>
              ))}
            </select>
            {pageLayout === 'timeline' && (
              <p className="text-[11px] text-orange-400/80 mb-4">Timeline is chronological by nature, so it always shows newest first.</p>
            )}

            <Button
              variant="primary"
              onClick={handleSaveLayout}
              disabled={isSavingLayout}
              className="w-full mt-3"
            >
              {isSavingLayout ? 'Saving…'
                : layoutSaveState === 'saved' ? <><SafeIcon icon={FiCheckCircle} /> Saved!</>
                : layoutSaveState === 'error' ? <><SafeIcon icon={FiAlertTriangle} /> Failed — try again</>
                : 'Save Layout'}
            </Button>
          </div>

          {/* Account Badges Status Card */}
          <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
                <SafeIcon icon={FiAward} className="text-orange-400 text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-50">Account Badges</h3>
                <p className="text-sm text-zinc-400">Unlock these to build more trust.</p>
              </div>
            </div>

            <div className="space-y-3">
              {badges.map((badge) => (
                <div 
                  key={badge.id}
                  className={`flex items-start gap-4 p-4 rounded-xl bg-[#0a0500]/50 border relative group transition-colors ${
                    badge.unlocked ? 'border-orange-500/20' : 'border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${
                    badge.unlocked ? badge.colorClass : 'bg-zinc-800 border-zinc-700 text-zinc-500'
                  }`}>
                    <SafeIcon icon={badge.icon} className="text-lg" />
                  </div>
                  <div className="pr-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`text-sm font-medium ${badge.unlocked ? 'text-amber-50' : 'text-zinc-400'}`}>{badge.name}</h4>
                      {!badge.unlocked && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">Locked</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">{badge.description}</p>
                  </div>
                  {!badge.unlocked && <SafeIcon icon={FiLock} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-700" />}
                </div>
              ))}
            </div>
          </div>

          {/* Tips Card */}
          <div className="p-6 rounded-2xl bg-zinc-900/20 border border-white/5 border-dashed flex items-start gap-4">
            <SafeIcon icon={FiEye} className="text-zinc-500 text-xl mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-amber-50 font-medium mb-1">Customize Your Page</h4>
              <p className="text-sm text-zinc-400 leading-relaxed mb-3">
                You can configure the SEO metadata, Open Graph image, and page title from the <span className="text-orange-400">SEO</span> tab in your dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Public Link + Preview.
            Sticks flush under the dashboard's h-20 (5rem) sticky header —
            top-0 would tuck it behind the header, so match the header height. */}
        <div className="space-y-6 lg:sticky lg:top-20">
          {/* Public Link Card */}
          <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20">
                <SafeIcon icon={FiGlobe} className="text-orange-400 text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-50">Your Public Link</h3>
                <p className="text-sm text-zinc-400">Share this link directly on social media or via email.</p>
              </div>
            </div>

            <div className="relative mb-6">
              <input
                type="text"
                readOnly
                value={publicUrl}
                className="w-full bg-[#0a0500]/80 border border-orange-500/20 rounded-xl pl-4 pr-24 py-3.5 text-amber-100/80 outline-none"
              />
              <button
                onClick={handleCopy}
                disabled={!profile?.public_id}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg text-sm font-medium transition-colors duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={copied ? FiCheckCircle : FiCopy} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <a
              href={profile?.public_id ? `https://moment.reviews/p/${profile.public_id}` : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button variant="primary" className="w-full" disabled={!profile?.public_id}>
                <SafeIcon icon={FiExternalLink} /> Open Public Page
              </Button>
            </a>
          </div>

          {/* Preview Wrapper */}
          <div className="relative rounded-2xl border border-orange-500/20 bg-[#0a0500] overflow-hidden shadow-[0_0_40px_rgba(234,88,12,0.05)] h-[440px] flex flex-col">
          {/* Fake Browser Toolbar */}
          <div className="h-12 bg-zinc-900/80 border-b border-orange-500/10 flex items-center px-4 gap-2 shrink-0">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
              <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
              <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
            </div>
            <div className="mx-auto px-4 py-1 rounded-md bg-zinc-950 border border-white/5 text-[10px] text-zinc-500 font-mono w-64 text-center truncate">
              {profile?.public_id ? `moment.reviews/p/${profile.public_id}` : 'Setup your Public ID'}
            </div>
          </div>

          {/* Preview Image/Container */}
          <div className="flex-1 bg-zinc-950 relative overflow-hidden flex items-start justify-center pt-8">
            {seo?.og_image_url ? (
              <img 
                src={seo.og_image_url} 
                alt="SEO Preview" 
                className="absolute inset-0 w-full h-full object-cover opacity-20" 
              />
            ) : (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-600/10 blur-[100px] rounded-full pointer-events-none"></div>
            )}
            
            <div className="z-10 p-6 w-full max-w-md mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-base font-bold text-amber-50 mb-1 truncate">
                  {seo?.meta_title || defaultTitle}
                </h3>
                <p className="text-xs text-zinc-500 max-w-[280px] mx-auto line-clamp-2">
                  {seo?.meta_description || defaultDesc}
                </p>
              </div>

              {/* Placeholder preview of the selected layout */}
              <LayoutPreview layout={pageLayout} />

              <p className="text-center text-[10px] text-zinc-600 mt-5 uppercase tracking-wider">
                {PAGE_LAYOUTS.find(l => l.value === pageLayout)?.label} layout preview
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MyPage;